/**
 * Offline punch outbox. When /api/timeclock/clockIn|Out fails because the
 * device has no network, the punch is enqueued here (in localStorage) and
 * surfaced as a "queued offline, will sync" toast. On reconnect (or app
 * boot) we POST every queued entry to /api/timeclock/replayOutbox; the
 * server dedupes via `clientGeneratedId`, so retrying the same batch
 * never produces duplicate ClockEvents.
 *
 * Storage shape (key: `dinengo.punchOutbox.v1`):
 *   [
 *     {
 *       clientGeneratedId: string,   // crypto.randomUUID at enqueue time
 *       action: 'clockIn'|'clockOut',
 *       username: string,
 *       pin?: string,
 *       password?: string,
 *       occurredAt: string,          // ISO timestamp from the device clock
 *     },
 *     ...
 *   ]
 *
 * Per-entry replay statuses (mirrors timeclock.service.replayOutbox):
 *   - committed | duplicate  → drop the entry, server has it.
 *   - rejected (REPLAY_TOO_OLD) → drop the entry, no point retrying.
 *   - rejected (any other reason) → KEEP the entry so a future flush can
 *     pick it up after the underlying issue (bad PIN, no-open-shift, etc.)
 *     is resolved out-of-band.
 */
import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';

export interface QueuedPunch {
  clientGeneratedId: string;
  action: 'clockIn' | 'clockOut';
  username: string;
  pin?: string;
  password?: string;
  occurredAt: string;
}

export interface ReplayResultEntry {
  clientGeneratedId: string;
  status: 'committed' | 'duplicate' | 'rejected';
  reason?: string;
  alreadyOpen?: boolean;
}

export interface ReplayOutboxResponse {
  success: boolean;
  results?: ReplayResultEntry[];
  summary?: {[k: string]: number};
}

const STORAGE_KEY = 'dinengo.punchOutbox.v1';

@Injectable({providedIn: 'root'})
export class PunchOutboxService implements OnDestroy {
  private onlineListener: (() => void) | null = null;
  private flushing = false;

  constructor(private http: HttpClient, private zone: NgZone) {
    // Auto-drain when the device comes back online. Keep the listener on
    // the window so navigator's offline events are still observed even
    // when no component is listening. NgZone reentry so any HTTP error
    // path (toast, change detection) runs inside Angular as expected.
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      this.onlineListener = () => {
        this.zone.run(() => {
          this.flush().catch(() => { /* swallowed — flush() already handles per-entry errors */ });
        });
      };
      window.addEventListener('online', this.onlineListener);
    }
  }

  ngOnDestroy(): void {
    if (this.onlineListener && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
  }

  /** True when the browser thinks it has connectivity. Cheap, no I/O. */
  isOnline(): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') { return true; }
    return navigator.onLine;
  }

  /**
   * Append a punch to the outbox. clientGeneratedId is generated by the
   * caller (so retries reuse the same id and the server dedupes for us).
   */
  enqueue(punch: QueuedPunch): void {
    if (!punch || !punch.clientGeneratedId) { return; }
    const queue = this.peek();
    // Replace-on-collision so a retry with the same id doesn't duplicate.
    const idx = queue.findIndex(q => q.clientGeneratedId === punch.clientGeneratedId);
    if (idx >= 0) { queue[idx] = punch; } else { queue.push(punch); }
    this.write(queue);
  }

  /** Snapshot of the queued punches. Returns a fresh array — safe to mutate. */
  peek(): QueuedPunch[] {
    if (typeof localStorage === 'undefined') { return []; }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { return []; }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  /**
   * POST the queue to /api/timeclock/replayOutbox. On 2xx, removes
   * committed/duplicate/REPLAY_TOO_OLD entries from the queue. Anything
   * else (rejected with a non-too-old reason) stays so a manager can fix
   * it server-side and the next flush picks it up.
   *
   * Resolves to the server response (or null on transport failure / empty
   * queue) so callers can surface a count if they want.
   */
  async flush(): Promise<ReplayOutboxResponse | null> {
    if (this.flushing) { return null; }
    const queue = this.peek();
    if (queue.length === 0) { return null; }
    if (!this.isOnline()) { return null; }
    this.flushing = true;
    try {
      const body = {
        punches: queue.map(q => ({
          clientGeneratedId: q.clientGeneratedId,
          type: q.action === 'clockOut' ? 'CLOCK_OUT' : 'CLOCK_IN',
          occurredAt: q.occurredAt,
          username: q.username,
          ...(q.pin ? {pin: q.pin} : {}),
          ...(q.password ? {password: q.password} : {}),
        })),
      };
      const response = await this.http
        .post<ReplayOutboxResponse>(`${environment.apiUrl}/timeclock/replayOutbox`, body)
        .toPromise()
        .catch((err) => {
          // Transport failure (still offline / 5xx). Leave the queue alone
          // — the next online event or app boot will retry.
          throw err;
        });
      const results: ReplayResultEntry[] = (response && response.results) || [];
      const drop = new Set<string>();
      results.forEach(r => {
        if (!r || !r.clientGeneratedId) { return; }
        if (r.status === 'committed' || r.status === 'duplicate') {
          drop.add(r.clientGeneratedId);
        } else if (r.status === 'rejected' && r.reason === 'REPLAY_TOO_OLD') {
          // No point retrying — drop and let the manager edit by hand.
          drop.add(r.clientGeneratedId);
        }
      });
      if (drop.size > 0) {
        const remaining = this.peek().filter(q => !drop.has(q.clientGeneratedId));
        this.write(remaining);
      }
      return response || null;
    } catch (_) {
      return null;
    } finally {
      this.flushing = false;
    }
  }

  /** Test/cleanup hook — wipe the queue. Production code never calls this. */
  clear(): void {
    this.write([]);
  }

  private write(queue: QueuedPunch[]): void {
    if (typeof localStorage === 'undefined') { return; }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (_) {
      // Storage quota exhausted — nothing useful we can do here. The next
      // call will see the prior state, which is the safer fallback than
      // silently dropping punches.
    }
  }
}
